import {SlackNotificationSettings} from "../../api/Model";
import React, {useCallback, useMemo} from "react";
import {Chip, IconButton, Stack, TableCell, Tooltip, Typography} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearAllIcon from '@mui/icons-material/ClearAll';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import {TableColumnData} from "../../components/data-table/TableColumnData";
import {DataTable} from "../../components/data-table/DataTable";
import {idFn} from "../../util/FunctionUtil";
import {cronDescription} from "../../components/cron-field/CronField";

function ActionColumn({setting}: { setting: SlackNotificationSettings }) {
    const context = React.useContext(SlackSettingsActionContext);
    if (!context) throw new Error('SlackSettingsActionContext not found');
    const {
        canAdmin,
        onEdit: edit,
        onDelete: remove,
        onClearState: clearState,
        onSendTest: sendTest,
    } = context;

    const handleEdit = useCallback(() => edit(setting), [edit, setting]);
    const handleDelete = useCallback(() => remove(setting), [remove, setting]);
    const handleClearState = useCallback(() => clearState(setting), [clearState, setting]);
    const handleSendTest = useCallback(() => sendTest(setting), [sendTest, setting]);

    return canAdmin ? (
        <TableCell align="right">
            <Tooltip title="Send Test Notification">
                <IconButton color="success" onClick={handleSendTest}>
                    <PlayArrowIcon/>
                </IconButton>
            </Tooltip>
            <Tooltip title="Clear State">
                <IconButton color="warning" onClick={handleClearState}>
                    <ClearAllIcon/>
                </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
                <IconButton color="primary" onClick={handleEdit}>
                    <EditIcon/>
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
                <IconButton color="error" onClick={handleDelete}>
                    <DeleteIcon/>
                </IconButton>
            </Tooltip>
        </TableCell>
    ) : <TableCell/>;
}

function CronCell({cron}: { cron: string }) {
    const description = useMemo(() => {
        return cronDescription(cron);
    }, [cron]);
    return (
        <TableCell sx={{color: description.type === 'error' ? 'error.main' : undefined}}>
            {description.type === 'success' ? description.description || cron : description.error}
        </TableCell>
    );

}

function LastRunCell({lastRunAt}: {
    lastRunAt: number | null,
}) {
    const formattedLastRun = useMemo(() => {
        return lastRunAt
            ? new Date(lastRunAt).toLocaleString('ru-RU')
            : 'Never';
    }, [lastRunAt]);

    return (
        <TableCell>
            <Typography variant="caption" color="text.secondary" title="Last run time">
                {formattedLastRun}
            </Typography>
        </TableCell>
    );

}

const columns: TableColumnData<SlackNotificationSettings>[] = [
    {
        key: 'enabled',
        header: '',
        width: '4em',
        align: 'center',
        cell: setting => (
            <TableCell valign="middle">
                {setting.enabled ? (
                    <Tooltip title="Enabled">
                        <CheckCircleIcon color="success" sx={{marginTop: '6px'}}/>
                    </Tooltip>
                ) : (
                    <Tooltip title="Disabled">
                        <CancelIcon color="disabled" sx={{marginTop: '6px'}}/>
                    </Tooltip>
                )}
            </TableCell>
        ),
    },
    {
        key: 'name',
        header: 'Name',
        cell: setting => (
            <TableCell>
                <Stack direction="row" gap={1} alignItems="center">
                    {setting.name}
                </Stack>
            </TableCell>
        ),
    },
    {
        key: 'problemFilter',
        header: 'Problem Filter',
        cell: setting => (
            <TableCell>
                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    {setting.problemTypes.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">All problems</Typography>
                    ) : (setting.problemTypes.map(problemType => (
                        <Chip key={problemType} size="small" label={problemType}/>
                    )))}
                </Stack>
            </TableCell>
        ),
    },
    {
        key: 'channelId',
        header: 'Slack Channel',
        cell: setting => <TableCell>{setting.channelId}</TableCell>,
    },
    {
        key: 'cron',
        header: 'Period',
        cell: setting => <CronCell cron={setting.cron}/>,
    },
    {
        key: 'lastRunAt',
        header: 'Last Run at',
        cell: setting => <LastRunCell lastRunAt={setting.lastRunAt}/>,
    },
    {
        key: 'actions',
        header: 'Actions',
        width: '15em',
        align: 'right',
        cell: setting => <ActionColumn setting={setting}/>,
    },
];

type SlackSettingsActions = {
    canAdmin: boolean;
    onEdit: (settings: SlackNotificationSettings) => void;
    onDelete: (settings: SlackNotificationSettings) => void;
    onClearState: (settings: SlackNotificationSettings) => void;
    onSendTest: (settings: SlackNotificationSettings) => void;
}

const SlackSettingsActionContext = React.createContext<SlackSettingsActions | undefined>(undefined);

export function SlackSettingsTable({rows, onEdit, onDelete, onClearState, onSendTest, canAdmin}: {
    rows: SlackNotificationSettings[],
    onEdit: (settings: SlackNotificationSettings) => void,
    onDelete: (settings: SlackNotificationSettings) => void,
    onClearState: (settings: SlackNotificationSettings) => void,
    onSendTest: (settings: SlackNotificationSettings) => void,
    canAdmin: boolean,
}) {
    return (
        <SlackSettingsActionContext.Provider value={{canAdmin, onEdit, onDelete, onClearState, onSendTest}}>
            <DataTable
                id={idFn}
                columns={columns}
                rows={rows}
            />
        </SlackSettingsActionContext.Provider>
    );
}
